import { useParams, useNavigate, useLocation } from "react-router-dom"
import { useGetModelFamilyModelsMetadataModelFamilyModelsModelFamilyIdGet, useGetResultsetPredictionsMetadataResultsetPredictionsResultsetIdGet, useGetResultsetsResultsetGet, useGetResultsetV2ResultsetDataframeV2ResultsetIdGet, useGetSinglePredictionResultsetsResultsetSinglePredictionGet } from "../../../api/generated/fastAPI";
import { useEffect, useMemo, useState, MouseEvent } from "react";
import { Model, Prediction, Resultset } from "../../../api/models";
import { IndividualSmilesMoleculeHighlightingType } from "./IndividualResultset";
import SinglePageHeader from "../../../components/Page/SinglePageHeader";
import useMessage from "../../../hooks/useMessage";
import SnackBar from "../../../components/Alerts/SnackBar";
import HorizontalNumberSlider from "../../../components/Data/HorizontalNumberSlider";
import { ReactComponent as RecordsIcon } from '../../../assets/Icons/adornments/Records.svg';
import MoleculeHighlightingGrid from "../../../components/Grid/MoleculeHighlightingGrid";
import { DropdownOption } from "../../../components/Menus&Dropdowns/Dropdown";
import GenerateReadAcrossModal from "../../ReadAcross/components/GenerateReadAcrossModal";
import SingleCompoundPredictionModal from "../components/SingleCompoundPredictionModal";
import NestedDropdown from "../../../components/Menus&Dropdowns/NestedDropdown";
import { useMoleculeHighlighting } from "../../../hooks/endpoints/useMoleculeHighlighting";


export interface PredictionDataWithMoleculeHighlighting {
    id: string,
    name: string,
    moleculeHighlightingData: IndividualSmilesMoleculeHighlightingType[],
    smiles: string,
    score: string | number | number[],
}

export default function IndividualResultsetPrediction() {
    const { resultset_id, model_family_id, smiles } = useParams();
    const navigate = useNavigate();

    const {
        open,
        setOpen,
        message,
        isSuccess,
        setPopupMessage
    } = useMessage();

    const [ resultset, setResultset ] = useState<Resultset | null>(null);
    const [ predictions, setPredictions ] = useState<Prediction[]>([]);
    const [ models, setModels ] = useState<Model[]>([]);
    const [ predictionData, setPredictionData ] = useState<{
        id: string,
        name: string,
        moleculeHighlightingData: IndividualSmilesMoleculeHighlightingType[],
        smiles: string,
        score: string | number | number[],
    }[]>([]);
    const [ threshold, setThreshold ] = useState<number>(0.5);
    const [ isConformal, setIsConformal ] = useState(false);

    const { pathname } = useLocation();

    const getResultset = useGetResultsetsResultsetGet({
        resultset_id: resultset_id ? resultset_id : '',
    });

    const getSingleResultset = useGetSinglePredictionResultsetsResultsetSinglePredictionGet({
        resultset_id: resultset_id ? resultset_id : '',
    })

    const getResultsetPredictionMetadata = useGetResultsetPredictionsMetadataResultsetPredictionsResultsetIdGet(
        resultset_id ? Number(resultset_id) : 0,
        { model_family_id: model_family_id ? Number(model_family_id) : 0 },
    );

    const getResultsetDataframe = useGetResultsetV2ResultsetDataframeV2ResultsetIdGet(resultset_id ? Number(resultset_id) : 0, {
        include_molecule_highlighting: false,
    });

    const getModelFamilyModels = useGetModelFamilyModelsMetadataModelFamilyModelsModelFamilyIdGet(model_family_id ? Number(model_family_id) : 0);

    const smilesIndex = useMemo(() => {
        if (!getResultsetDataframe.data || !smiles) return -1;
        const data = getResultsetDataframe.data as unknown as any;
        const smilesStrings = data["structure/SMILES"] as string[] | undefined;
        if (!smilesStrings) return -1;
        return smilesStrings.indexOf(smiles);
    }, [getResultsetDataframe.data, smiles]);

    const moleculeHighlightingQuery = useMoleculeHighlighting(
        {
            resultsetId: resultset_id ? Number(resultset_id) : 0,
            moleculeIndex: smilesIndex >= 0 ? smilesIndex : null,
        },
        !!resultset_id && smilesIndex >= 0,
    );

    const [ isLoading, setIsLoading ] = useState(false);

    useEffect(() => {
        if (getResultset.isLoading || getResultsetPredictionMetadata.isLoading || getModelFamilyModels.isLoading || getResultsetDataframe.isLoading) {
            setIsLoading(true);
        } else {
            setIsLoading(false);
        }
    }, [
        getResultset.isLoading,
        getResultsetPredictionMetadata.isLoading,
        getModelFamilyModels.isLoading,
        getResultsetDataframe.isLoading,
    ]);

    useEffect(() => {
        if (pathname.includes('compound') && getSingleResultset.data) {
            const data = getSingleResultset.data as unknown as Resultset;
            setResultset(data);
        } else if (!pathname.includes('compound') && getResultset.data) {
            const data = getResultset.data as unknown as Resultset;
            setResultset(data);
        }
    }, [getResultset.data, getSingleResultset.data, pathname]);


    useEffect(() => {
        if (getResultsetPredictionMetadata.data) {
            const data =  getResultsetPredictionMetadata.data as unknown as Prediction[];
            setPredictions(data);

            const predType = data[0].prediction_metadata!.prediction_type!;
            if (predType !== 'normal') {
                setIsConformal(true);
            } else {
                setIsConformal(false);
            }
        }
    }, [getResultsetPredictionMetadata.data]);

    useEffect(() => {
        if (getModelFamilyModels.data) {
            const data = getModelFamilyModels.data as unknown as Model[];
            setModels(data);
        }
    }, [getModelFamilyModels.data]);

    useEffect(() => {
        if (getResultsetDataframe.data) {
            const data = getResultsetDataframe.data as unknown as any;

            const predData: PredictionDataWithMoleculeHighlighting[] = [];
            
            let model_hashes: string[] = [];
            
            const model_family_mapping = data.model_family_mapping;
            if (model_family_mapping && model_family_id !== undefined && model_family_mapping[Number(model_family_id)]) {
                const model_family_model_hashes = model_family_mapping[Number(model_family_id)].model_hashes as string[] | undefined;
                if (model_family_model_hashes && model_family_model_hashes.length > 0) {
                    model_hashes = model_family_model_hashes;
                }
            };

            if (model_hashes.length > 0 && models.length > 0) {
                const model_hash_mapping = data.model_hash;
                const model_hashes_and_names: { name: string, hash: string }[] = [];
                model_hashes.forEach(hash => {
                    const matchingModel = models.find(model => model.file_hash! === hash);
                    if (matchingModel) {
                        const name = matchingModel.model_metadata!.training_metadata!.job_metadata!.cv_model!.ml_base_cls!.name!;
                        model_hashes_and_names.push({ name, hash })
                    }
                });

                model_hashes_and_names.sort((a,b) => {
                    if (a.name < b.name) {
                        return -1;
                    } else if (a.name > b.name) {
                        return 1;
                    } else {
                        return 0;
                    }
                })

                const mhData = moleculeHighlightingQuery.data;

                if (model_hashes_and_names.length > 0 && smilesIndex !== -1 && model_hash_mapping && Object.keys(model_hash_mapping).length > 0) {
                    const model_hash_mapping_keys = Object.keys(model_hash_mapping);
                    model_hashes_and_names.forEach(model_hash_and_name => {
                        const matchingModelHashKey = model_hash_mapping_keys.find(k => k === model_hash_and_name.hash);
                        if (matchingModelHashKey) {
                            const matchingModelHashValue = model_hash_mapping[matchingModelHashKey];

                            let score: string | number | number[] = '';
                            const moleculeHighlightingData: IndividualSmilesMoleculeHighlightingType[] = [];

                            const predictions = matchingModelHashValue.predictions;

                            if (predictions) {
                                const predArr = predictions.y_pred_proba ? predictions.y_pred_proba : predictions.y_conf_pred ? predictions.y_conf_pred : [];
                                if (predArr.length > 0) {
                                    score = predArr[smilesIndex];
                                }
                            }

                            // Use MH data from the dedicated endpoint
                            const modelMh = mhData?.[model_hash_and_name.hash];
                            if (modelMh?.molecule_highlighting) {
                                modelMh.molecule_highlighting.forEach(h => {
                                    if (h.atom_bond_importance && h.metadata) {
                                        moleculeHighlightingData.push({
                                            dataframe: {
                                                atom_bond_importance: h.atom_bond_importance,
                                                smiles: smiles!,
                                            },
                                            metadata: h.metadata,
                                        })
                                    }
                                })
                            }

                            if (moleculeHighlightingData.length > 0) {
                                predData.push({
                                    id: model_hash_and_name.hash,
                                    name: model_hash_and_name.name,
                                    moleculeHighlightingData,
                                    smiles: smiles!,
                                    score,
                                })
                            }
                        }
                    })
                }
            };

            setPredictionData(predData);
        }
    }, [
        getResultsetDataframe.data, 
        smiles, 
        smilesIndex,
        model_family_id,
        models,
        moleculeHighlightingQuery.data,
    ]);

    const dropdownOptions: DropdownOption[] = [
        {
            label: 'Run Read-Across',
            btnComp: (
                <GenerateReadAcrossModal 
                    setPopupMessage={setPopupMessage}
                    location="dropdown"
                    selectedCompound={smiles}
                />
            )
        },
        {
            label: 'Run Prediction',
            btnComp: (
                <SingleCompoundPredictionModal 
                    setPopupMessage={setPopupMessage}
                    location="dropdown"
                    selectedCompound={smiles}
                />
            )
        },
        
    ];


    return (
        <div className="p-10 h-full flex flex-col justify-start items-stretch gap-4">        
            <SinglePageHeader 
                id='single-resultset-header' 
                label={smiles ? smiles : ''} 
                isLoading={isLoading} 
                pages={[
                    {
                        label: 'Resultsets',
                        action: (e: MouseEvent<HTMLButtonElement>) => {navigate('/resultsets')}
                    },
                    {
                        label: resultset ? resultset.name! : '',
                        action: (e: MouseEvent<HTMLButtonElement>) => {
                            let url = '/resultsets';
                            if (!pathname.includes('compound')) {
                                url = `/resultsets/${resultset ? resultset.id! : ''}`
                            } else {
                                url = `/resultsets/compound/${resultset ? resultset.id! : ''}`
                            }
                            navigate(url);
                        }
                    },
                    {
                        label: 'Molecule Highlighting'
                    }
                ]}
                itemMetadata={[{
                    label: 'Records',
                    value: predictions.length > 0 ? predictions.length.toString() : '0',
                    adornment: <RecordsIcon/>,
                }]}
            >
                <NestedDropdown
                    dir="right"
                    btn={{
                        label: 'Options',
                        type: 'primary',
                        styling: 'solid',
                        id: 'single-resultset-prediction-options'
                    }}
                    options={dropdownOptions}
                />
            </SinglePageHeader>


            <div className="w-full flex justify-start items-center gap-4">
                <div className="flex justify-end items-center gap-4">
                    {isConformal && (
                    <HorizontalNumberSlider 
                        label="Alpha Threshold"
                        threshold={threshold}
                        setThreshold={setThreshold}
                        hasBorderAndPadding
                    />
                    )}
                </div>
            </div>

            <MoleculeHighlightingGrid 
                isLoading={isLoading}
                data={predictionData}
                threshold={isConformal ? threshold : null}
                setPopupMessage={setPopupMessage}
            />
            
            <SnackBar open={open} setOpen={setOpen} message={message} isSuccess={isSuccess}/>  
            
    </div>
    )
}